import React, { FC, useEffect, useState } from 'react';
import type {
  OrganisationAdmin,
  OrganisationExternalInvite,
  OrganisationGroup,
  OrganisationMember,
  OrganisationMemberRole,
  OrganisationSeatKind,
} from '@dreamai/types';
import {
  SEAT_LABELS,
  formatDate,
  formatLastActive,
  selectableSeatKinds,
} from './organisationLabels';
import { ReactComponent as ChevronDownIcon } from '../../icons/chevron-down.svg';
import './organisation-roster.scss';

export type RosterRole = OrganisationMemberRole;

type RosterStatus =
  | 'you'
  | 'accepted'
  | 'invited'
  | 'not-delivered'
  | 'deleting'
  | 'deactivated'
  | 'deleted'
  | 'declined';

const STATUS_TEXT: Record<RosterStatus, string> = {
  you: 'You',
  accepted: 'Accepted',
  invited: 'Invited',
  'not-delivered': 'Not delivered',
  deleting: 'Deleting',
  deactivated: 'Deactivated',
  deleted: 'Removed',
  declined: 'Declined',
};

const STATUS_ORDER: Record<RosterStatus, number> = {
  you: 0,
  accepted: 1,
  invited: 2,
  'not-delivered': 3,
  deleting: 4,
  deactivated: 5,
  declined: 6,
  deleted: 7,
};

/**
 * One person on the Members tab. Seats, Dream invitations and admin logins
 * are separate records in the backend; a row joins whichever of them
 * belong to the same person (admins match seats by login, external invites
 * by email).
 */
export interface RosterRow {
  key: string;
  name: string;
  contact?: string;
  role: RosterRole;
  status: RosterStatus;
  isMe: boolean;
  member?: OrganisationMember;
  admin?: OrganisationAdmin;
  invite?: OrganisationExternalInvite;
}

function lastActivePhrase(iso: string | undefined): string {
  if (!iso) return 'Not active yet';
  const label = formatLastActive(iso);
  if (label === 'Today' || label === 'Yesterday') {
    return `Last active ${label.toLowerCase()}`;
  }
  return label.endsWith('ago')
    ? `Last active ${label}`
    : `Last active ${formatDate(iso)}`;
}

function memberStatus(member: OrganisationMember, isMe: boolean): RosterStatus {
  if (!member.userId) return 'deleted';
  if (member.deletionScheduledAt && member.userId) return 'deleting';
  if (member.status === 'active') return isMe ? 'you' : 'accepted';
  return member.status;
}

/** "m:ss" left until `deadline`, ticking every second; "0:00" once it has passed. */
export function formatCountdown(deadline: string, now: number): string {
  const left = Math.max(0, Math.ceil((Date.parse(deadline) - now) / 1000));
  const minutes = Math.floor(left / 60);
  const seconds = left % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const DeletionCountdown: FC<{ deadline: string }> = ({ deadline }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return (
    <span className='organisation-roster-status status-deleting'>
      {STATUS_TEXT.deleting} {formatCountdown(deadline, now)}
    </span>
  );
};

export function buildRosterRows(
  members: OrganisationMember[],
  externalInvites: OrganisationExternalInvite[],
  admins: OrganisationAdmin[],
  myMembershipId: string | undefined,
): RosterRow[] {
  const current = admins.filter((a) => a.status !== 'removed');
  const claimed = new Set<string>();
  const adminFor = (match: (a: OrganisationAdmin) => boolean) => {
    const admin = current.find((a) => !claimed.has(a.id) && match(a));
    if (admin) claimed.add(admin.id);
    return admin;
  };

  const rows: RosterRow[] = members.map((member: OrganisationMember) => {
    const admin = member.userId
      ? adminFor((a) => a.auth0Sub === member.userId)
      : undefined;
    const isMe = member.id === myMembershipId;
    return {
      key: `member-${member.id}`,
      name: member.name || 'Unnamed member',
      contact: admin?.email,
      role: admin ? 'admin' : (member.role ?? 'member'),
      status: memberStatus(member, isMe),
      isMe,
      member,
      admin,
    };
  });

  for (const invite of externalInvites) {
    const admin =
      invite.channel === 'email'
        ? adminFor((a) => a.email === invite.address)
        : undefined;
    rows.push({
      key: `invite-${invite.id}`,
      name: invite.name || invite.address,
      contact: invite.name ? invite.address : undefined,
      role: admin ? 'admin' : (invite.role ?? 'member'),
      status: invite.status === 'send_failed' ? 'not-delivered' : 'invited',
      isMe: false,
      invite,
      admin,
    });
  }

  for (const admin of current) {
    if (claimed.has(admin.id)) continue;
    rows.push({
      key: `admin-${admin.id}`,
      name: admin.name || admin.email || 'Admin',
      contact: admin.name ? admin.email : undefined,
      role: 'admin',
      status:
        admin.status === 'invited'
          ? 'invited'
          : admin.isMe
            ? 'you'
            : 'accepted',
      isMe: admin.isMe,
      admin,
    });
  }

  return rows.sort((a, b) => {
    const byStatus = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
    return byStatus !== 0 ? byStatus : a.name.localeCompare(b.name);
  });
}

interface MemberGroupsProps {
  member: OrganisationMember;
  groups: OrganisationGroup[];
  busy: boolean;
  editable: boolean;
  onChange: (groupIds: string[]) => void;
}

/** "Groups: Engineers, Partners · Edit" — expands into one checkbox per group. */
const MemberGroups: FC<MemberGroupsProps> = ({
  member,
  groups,
  busy,
  editable,
  onChange,
}) => {
  const [editing, setEditing] = useState(false);
  const mine = groups.filter((g) => g.membershipIds.includes(member.id));

  if (!editing) {
    return (
      <span
        className='organisation-roster-item-groups'
        data-testid='organisation-member-groups'
      >
        {mine.length > 0
          ? `Groups: ${mine.map((g) => g.name).join(', ')}`
          : 'No groups'}
        {editable && (
          <>
            {' · '}
            <button
              type='button'
              className='btn-link'
              disabled={busy}
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
          </>
        )}
      </span>
    );
  }

  return (
    <fieldset
      className='organisation-roster-item-groups editing'
      data-testid='organisation-member-groups'
      disabled={busy}
    >
      <legend className='field-label'>Groups for {member.name}</legend>
      <div className='organisation-roster-group-list'>
        {groups.map((group) => {
          const checked = group.membershipIds.includes(member.id);
          return (
            <label className='organisation-checkbox' key={group.id}>
              <input
                type='checkbox'
                checked={checked}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...mine.map((g) => g.id), group.id]
                      : mine.filter((g) => g.id !== group.id).map((g) => g.id),
                  )
                }
              />
              <span>{group.name}</span>
            </label>
          );
        })}
      </div>
      <button
        type='button'
        className='btn-link'
        onClick={() => setEditing(false)}
      >
        Done
      </button>
    </fieldset>
  );
};

interface OrganisationRosterProps {
  /** Groups are switched on for this organisation (the backend lists them) */
  groupsAvailable: boolean;
  members: OrganisationMember[];
  externalInvites: OrganisationExternalInvite[];
  /** Admin logins; only loaded for admins */
  admins: OrganisationAdmin[];
  isAdmin: boolean;
  /** The viewer's own seat, when they have one in this organisation */
  myMembershipId?: string;
  busyId: string | null;
  /** Organisation groups; admins can move members between them here */
  groups?: OrganisationGroup[];
  onChangeSeat: (
    member: OrganisationMember,
    seatKind: OrganisationSeatKind,
  ) => void;
  onChangeRole: (row: RosterRow, role: RosterRole) => void;
  onRemove: (row: RosterRow) => void;
  /** Cancels a pending deletion during its grace period */
  onRestore: (member: OrganisationMember) => void;
  onReactivate: (member: OrganisationMember) => void;
  /** Fresh invitation for a member who declined */
  onReinvite: (member: OrganisationMember) => void;
  /** Withdraws an invitation the invitee hasn't acted on */
  onUninvite: (row: RosterRow) => void;
  /** Replaces the member's groups with exactly `groupIds` */
  onChangeGroups?: (member: OrganisationMember, groupIds: string[]) => void;
}

const OrganisationRoster: FC<OrganisationRosterProps> = ({
  groupsAvailable,
  members,
  externalInvites,
  admins,
  isAdmin,
  myMembershipId,
  busyId,
  groups = [],
  onChangeSeat,
  onChangeRole,
  onRemove,
  onRestore,
  onReactivate,
  onReinvite,
  onUninvite,
  onChangeGroups,
}) => {
  // Members only see who is in the organisation; admins also see the
  // invitation pipeline, admin logins and deactivated accounts.
  const rows = isAdmin
    ? buildRosterRows(members, externalInvites, admins, myMembershipId)
    : buildRosterRows(
        members.filter((m) => m.status === 'active'),
        [],
        [],
        myMembershipId,
      );
  const activeAdmins = admins.filter((a) => a.status === 'active').length;
  const liveRows = rows.filter((row) => row.status !== 'deleted');
  const deletedRows = rows.filter((row) => row.status === 'deleted');
  const [showDeleted, setShowDeleted] = useState(false);
  const rosterClass = `organisation-roster${isAdmin ? ' admin-view' : ''}`;

  const renderRow = (row: RosterRow) => {
    const { member, admin, invite } = row;
    const busy =
      (!!member && busyId === member.id) ||
      (!!invite && busyId === invite.id) ||
      (!!admin && busyId === admin.id);
    const settled =
      row.status !== 'deactivated' &&
      row.status !== 'deleted' &&
      row.status !== 'declined' &&
      row.status !== 'deleting';
    const lastAdmin = admin?.status === 'active' && activeAdmins <= 1;
    // An admin role on a pending invitation is granted when they accept.
    const roleFromInvite = !admin && row.role === 'admin';
    const deletingAt =
      row.status === 'deleting' ? member?.deletionScheduledAt : undefined;
    const canRemove =
      !row.isMe &&
      !deletingAt &&
      ((!!member?.userId &&
        (member.status === 'active' || member.status === 'deactivated')) ||
        (!member && !invite && !!admin));
    // Someone part-way through the onboarding the invitation started
    // joins on completion, so it can't be withdrawn.
    const canUninvite =
      !!invite ||
      (member?.status === 'invited' &&
        !!member.userId &&
        !member.invitedViaOnboarding);
    const activity =
      member?.status === 'active'
        ? lastActivePhrase(member.lastActiveAt)
        : member?.status === 'deactivated' && row.status !== 'deleted'
          ? `Deactivated ${formatDate(member.deactivatedAt)}`
          : '';
    const testId = member
      ? 'organisation-member'
      : invite
        ? 'organisation-external-invite'
        : 'organisation-admin';

    return (
      <li
        key={row.key}
        className={`organisation-roster-item status-${row.status}`}
        data-testid={testId}
      >
        <div className='organisation-roster-item-info'>
          <span className='organisation-roster-item-name'>{row.name}</span>
          {isAdmin && row.status === 'deleted'
            ? member?.deactivatedAt && (
                <span className='organisation-roster-item-contact'>
                  Account deleted {formatDate(member.deactivatedAt)}
                </span>
              )
            : isAdmin &&
              row.contact && (
                <span className='organisation-roster-item-contact'>
                  {row.contact}
                </span>
              )}
          {isAdmin && member?.seatKind === 'directory' && (
            <select
              className='field-select compact'
              aria-label={`Seat for ${member.name}`}
              value={member.seatKind}
              disabled={!settled || busy}
              onChange={(e) =>
                onChangeSeat(member, e.target.value as OrganisationSeatKind)
              }
            >
              {selectableSeatKinds(member.seatKind).map((s) => (
                <option key={s} value={s}>
                  {SEAT_LABELS[s]}
                </option>
              ))}
            </select>
          )}
          {isAdmin &&
            member &&
            groupsAvailable &&
            onChangeGroups &&
            groups.length > 0 &&
            member.status !== 'declined' && (
              <MemberGroups
                member={member}
                groups={groups}
                busy={busy}
                editable={settled}
                onChange={(groupIds) => onChangeGroups(member, groupIds)}
              />
            )}
        </div>
        {isAdmin && (
          <>
            <span className='organisation-roster-item-activity'>
              {activity}
            </span>
            <span className='organisation-roster-role'>
              {settled && (
                <select
                  className='organisation-roster-role-select'
                  aria-label={`Role for ${row.name}`}
                  value={row.role}
                  disabled={
                    busy ||
                    roleFromInvite ||
                    (row.role === 'admin' && lastAdmin)
                  }
                  title={
                    roleFromInvite
                      ? 'Becomes an admin when they accept'
                      : row.role === 'admin' && lastAdmin
                        ? 'Every organisation needs at least one admin'
                        : undefined
                  }
                  onChange={(e) =>
                    onChangeRole(row, e.target.value as RosterRole)
                  }
                >
                  <option value='admin'>Admin</option>
                  <option value='member'>Member</option>
                </select>
              )}
            </span>
          </>
        )}
        <span className='organisation-roster-status-cell'>
          {(isAdmin || row.isMe) &&
            (deletingAt ? (
              <DeletionCountdown deadline={deletingAt} />
            ) : (
              <span
                className={`organisation-roster-status status-${row.status}`}
              >
                {STATUS_TEXT[row.status]}
              </span>
            ))}
        </span>
        {isAdmin && (
          <span className='organisation-roster-item-actions'>
            {canRemove && (
              <button
                type='button'
                className='organisation-roster-action'
                disabled={busy}
                onClick={() => onRemove(row)}
              >
                {busy ? '…' : 'Remove'}
              </button>
            )}
            {deletingAt && member && (
              <button
                type='button'
                className='organisation-roster-action'
                disabled={busy}
                onClick={() => onRestore(member)}
              >
                {busy ? '…' : 'Restore'}
              </button>
            )}
            {member?.status === 'deactivated' &&
              member.userId &&
              !deletingAt && (
                <button
                  type='button'
                  className='organisation-roster-action'
                  disabled={busy}
                  onClick={() => onReactivate(member)}
                >
                  {busy ? '…' : 'Reactivate'}
                </button>
              )}
            {canUninvite && (
              <button
                type='button'
                className='organisation-roster-action'
                disabled={busy}
                onClick={() => onUninvite(row)}
              >
                {busy ? '…' : 'Uninvite'}
              </button>
            )}
            {member?.status === 'declined' && member.userId && (
              <button
                type='button'
                className='organisation-roster-action'
                disabled={busy}
                onClick={() => onReinvite(member)}
              >
                {busy ? '…' : 'Invite again'}
              </button>
            )}
          </span>
        )}
      </li>
    );
  };

  return (
    <>
      <ul className={rosterClass} data-testid='organisation-roster'>
        {liveRows.map(renderRow)}
      </ul>
      {isAdmin && deletedRows.length > 0 && (
        <div className='organisation-roster-deleted'>
          <button
            type='button'
            className='organisation-roster-deleted-toggle'
            aria-expanded={showDeleted}
            onClick={() => setShowDeleted((open) => !open)}
          >
            <span className='organisation-roster-deleted-title'>
              Deleted members
            </span>
            <span className='organisation-roster-deleted-count'>
              {deletedRows.length}
            </span>
            <ChevronDownIcon
              aria-hidden='true'
              className={`organisation-roster-deleted-chevron${showDeleted ? ' open' : ''}`}
            />
          </button>
          {showDeleted && (
            <ul
              className={rosterClass}
              data-testid='organisation-deleted-roster'
            >
              {deletedRows.map(renderRow)}
            </ul>
          )}
        </div>
      )}
    </>
  );
};

export default OrganisationRoster;
